import * as React from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import { RvLabel } from '../../components/Label/Label';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { RvTimelineInfoCard, GetIconForDate } from './TimelineInfoCard';
import { filterString, filterDate } from '../../helpers/SearchFilterHelper';
import { MatterTerminology } from '../../MatterTerminology';
import { MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import EventSystem from '../../EventSystem';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        list: {
            display: 'flex',
            flexDirection: 'column',
        },
        noData: {
            textAlign: 'center',
            padding: theme.spacing(4, 0),
        },
    })
);

export interface MatterDate {
    id: string;
    description: string;
    matterDate: Date;
    type: string;
    completedDate: Date;
    dueDate: Date;
    completed: boolean;
    sortOrder: Number;
}

interface Data {
    loading?: boolean;
    networkStatus?: number;
    matter: {
        dates: {
            recordCount: Number;
            dates: Array<MatterDate>;
        };
    };
    refetch: () => void;
}

interface TimelineInfoStreamProps extends ExternalProps {
    filter: string;
    data: Data;
}

function convertDate(value: Date): Date | undefined {
    if (value) {
        return new Date(Date.parse(value.toString()));
    }
    return undefined;
}

function filterDates(date: MatterDate, filter: string) {
    var lowerFilter = filter.toLowerCase();
    if (filterString(date.description, lowerFilter) ||
        filterDate(date.dueDate, lowerFilter) ||
        filterDate(date.completedDate, lowerFilter) ||
        filterDate(date.matterDate, lowerFilter)) {
        return true;
    }
    var completed = date.completed;
    var completedDate = convertDate(date.completedDate);
    var dueDate = convertDate(date.dueDate);
    let result = GetIconForDate(completed, dueDate, completedDate);
    return filterString(result.stringDisplay, lowerFilter);
}

const RvTimelineInfoStream: React.FC<TimelineInfoStreamProps> = (props) => {
    const classes = useStyles();
    const refetch = props.data.refetch;

    React.useEffect(() => {
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, refetch);
        return () => { EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, refetch); };
    // tslint:disable-next-line: align
    }, [refetch]);

    if (props.data.loading) {
        return <SkeletonLoader variant="timeline" rows={4} />;
    }
    if (props.data.matter && props.data.matter.dates && props.data.matter.dates.recordCount) {
        var dates = props.data.matter.dates.dates;
        if (props.filter) {
            dates = dates.filter((date) => filterDates(date, props.filter));
        }
        return (
            <div className={classes.list}>
                {dates.map((date: MatterDate, idx: number) => (
                    <AnimatedItem key={idx} noFill={true} delay={idx * 30}>
                        <RvTimelineInfoCard data={date} isLast={idx === dates.length - 1} />
                    </AnimatedItem>
                ))}
            </div>
        );
    }
    return (
        <div className={classes.noData}>
            <RvLabel label={MatterTerminology.NoTimelineDatesSet} />
        </div>
    );
};

const TimelineData = gql`query TimelineData($id: Int!){
    matter(id: $id) {
        id,
        dates {
            recordCount
            dates {
                id
                description
                type
                completedDate
                dueDate
                completed
                sortOrder
            }
        }
    }
}`;

interface ExternalProps {
    id: string;
    filter?: string;
}

// tslint:disable-next-line:no-any
export const TimelineInfoStream = graphql<any, ExternalProps, any>
    (TimelineData, {
        // filter dropped from variables - TimelineData only declares $id, so filter was never
        // used server-side, just fragmenting Apollo's cache key per keystroke (filtering
        // happens client-side in filterDates above, reading this.props.filter directly).
        // fetchPolicy was inheriting the app's global default (cache-and-network) - always
        // re-fetching on every tab mount regardless of cache. Cached after the first load; the
        // header's Refresh button (MatterDetails.tsx) forces a re-fetch via
        // MATTER_DETAILS_REFRESH_EVENT.
        options: ({ id }) => ({ variables: { id: id }, fetchPolicy: 'cache-first' })
    })
    (RvTimelineInfoStream);
